/**
 * Availability Calendar Tab Component
 * Main component for the availability calendar tab
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, Tabs } from '@tracekit/core';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  Calendar as CalendarIcon,
  AlertTriangle,
  Search,
  UserPlus,
  Clock,
  Info,
  User,
} from 'lucide-react';
import { AvailabilityCalendar } from './AvailabilityCalendar';
import { CapacityAssessmentCard } from './CapacityAssessmentCard';
import { ConflictDetectionPanel } from './ConflictDetectionPanel';
import { AlternativeStaffSelector } from './AlternativeStaffSelector';
import { StaffRequestForm } from './StaffRequestForm';
import { StaffRequestWorkflow } from './StaffRequestWorkflow';

interface StaffMember {
  id: string;
  name: string;
  department?: string;
  designations?: string[];
  availability?: string;
}

interface StaffMemberEntity {
  id: string;
  entity_type?: string;
  data: {
    name?: string;
    department?: string;
    designations?: string[];
    availability?: string;
    [key: string]: any;
  };
  created_at?: string;
}

interface AvailabilityCalendarTabProps {
  projectId: string;
  staffMembers: StaffMemberEntity[] | StaffMember[];
}

export function AvailabilityCalendarTab({
  projectId,
  staffMembers,
}: AvailabilityCalendarTabProps) {
  // Transform staff members to the expected format
  const transformedStaffMembers: StaffMember[] = useMemo(() => {
    if (!staffMembers || staffMembers.length === 0) {
      return [];
    }
    
    return staffMembers.map((member) => {
      // Check if it's already in the expected format
      if ('name' in member && !('data' in member)) {
        return member as StaffMember;
      }
      
      // Transform from entity format
      const entity = member as StaffMemberEntity;
      return {
        id: entity.id,
        name: entity.data?.name || 'Unknown Staff',
        department: entity.data?.department,
        designations: Array.isArray(entity.data?.designations) ? entity.data.designations : [],
        availability: entity.data?.availability,
      };
    });
  }, [staffMembers]);

  const [selectedStaffId, setSelectedStaffId] = useState<string | undefined>();
  const [activeView, setActiveView] = useState<'calendar' | 'conflicts' | 'requests'>('calendar');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string | undefined>();
  const [conflictCheckStaffIds, setConflictCheckStaffIds] = useState<string[]>([]);
  const [conflictFilter, setConflictFilter] = useState<'all' | 'selected'>('all');

  // Get all staff member IDs for bulk conflict detection
  const allStaffIds = useMemo(() => {
    return transformedStaffMembers.map(s => s.id);
  }, [transformedStaffMembers]);

  // Automatically check conflicts for all staff when conflicts tab is opened
  useEffect(() => {
    if (activeView === 'conflicts') {
      // Default to checking all staff members
      if (conflictFilter === 'all') {
        setConflictCheckStaffIds(allStaffIds);
      } else if (conflictFilter === 'selected' && selectedStaffId) {
        setConflictCheckStaffIds([selectedStaffId]);
      }
    } else if (activeView !== 'conflicts') {
      // Clear conflict check when leaving conflicts tab
      setConflictCheckStaffIds([]);
    }
  }, [activeView, conflictFilter, selectedStaffId, allStaffIds]);

  // Fetch conflicts for selected staff (or all staff if bulk mode)
  const { data: conflictData, isLoading: conflictsLoading } = useQuery({
    queryKey: ['staff-conflicts', projectId, conflictCheckStaffIds.sort().join(',')],
    queryFn: async () => {
      if (conflictCheckStaffIds.length === 0) return null;
      const api = getAPIClient();
      const response = await api.post('/api/rfp/staff/conflicts', {
        project_id: projectId,
        staff_member_ids: conflictCheckStaffIds,
        conflict_threshold: 100.0,
        overlap_days_threshold: 1,
        warning_threshold: 90.0,
      });
      return response;
    },
    enabled: conflictCheckStaffIds.length > 0 && !!projectId,
  });

  // Fetch capacity for selected staff
  const { data: capacityData } = useQuery({
    queryKey: ['staff-capacity', projectId, selectedStaffId],
    queryFn: async () => {
      if (!selectedStaffId || !projectId) return null;
      const api = getAPIClient();
      const response = await api.get(`/api/rfp/staff/capacity/${selectedStaffId}`, {
        params: { project_id: projectId },
      });
      return response;
    },
    enabled: !!selectedStaffId && !!projectId,
  });

  const handleCheckConflicts = () => {
    if (conflictFilter === 'selected' && selectedStaffId) {
      setConflictCheckStaffIds([selectedStaffId]);
    } else if (conflictFilter === 'all') {
      setConflictCheckStaffIds(allStaffIds);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="w-7 h-7" />
            Availability Calendar
          </h2>
          <p className="text-gray-600 dark:text-theme-text-secondary mt-1">
            View staff capacity, detect conflicts, and manage resource planning
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              handleCheckConflicts();
              setActiveView('conflicts');
            }}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Check Conflicts
          </Button>
          <Button
            onClick={() => {
              setShowRequestForm(true);
              setActiveView('requests');
            }}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Request Staff
          </Button>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        tabs={[
          {
            id: 'calendar',
            label: 'Calendar & Capacity',
            content: (
              <div className="space-y-6">
                {transformedStaffMembers.length === 0 ? (
                  <Card className="p-8">
                    <div className="text-center">
                      <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-2">
                        No Staff Members Found
                      </h3>
                      <p className="text-gray-600 dark:text-theme-text-secondary">
                        Staff members need to be loaded from the Staff Data tab first.
                      </p>
                    </div>
                  </Card>
                ) : (
                  <>
                    <AvailabilityCalendar
                      projectId={projectId}
                      staffMembers={transformedStaffMembers}
                      selectedStaffId={selectedStaffId}
                      onStaffSelect={setSelectedStaffId}
                    />
                    {capacityData && (
                      <CapacityAssessmentCard
                        capacityData={capacityData}
                        showDetails={true}
                      />
                    )}
                  </>
                )}
              </div>
            ),
          },
          {
            id: 'conflicts',
            label: 'Conflict Detection',
            content: (
              <div className="space-y-6">
                {/* Filter Controls */}
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold mb-2">Conflict Detection Scope</h3>
                      <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                        Check conflicts across all staff members or filter to a specific staff member
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={conflictFilter === 'all' ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setConflictFilter('all');
                          setConflictCheckStaffIds(allStaffIds);
                        }}
                      >
                        All Staff ({allStaffIds.length})
                      </Button>
                      <Button
                        variant={conflictFilter === 'selected' ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setConflictFilter('selected');
                          if (selectedStaffId) {
                            setConflictCheckStaffIds([selectedStaffId]);
                          }
                        }}
                        disabled={!selectedStaffId}
                      >
                        Selected Staff Only
                      </Button>
                    </div>
                  </div>
                </Card>

                {conflictCheckStaffIds.length === 0 ? (
                  <Card className="p-8">
                    <div className="text-center">
                      <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-theme-text-secondary">
                        {conflictFilter === 'selected' && !selectedStaffId
                          ? 'Select a staff member from the calendar to check conflicts.'
                          : 'No staff members available for conflict detection.'}
                      </p>
                    </div>
                  </Card>
                ) : (
                  <>
                    <ConflictDetectionPanel
                      conflictData={conflictData}
                      isLoading={conflictsLoading}
                      onFindAlternatives={(staffId, reqId) => {
                        setSelectedRequirementId(reqId);
                        setActiveView('alternatives');
                      }}
                      onAdjustTimeline={(staffId) => {
                        // TODO: Implement timeline adjustment UI
                        alert('Timeline adjustment feature coming soon');
                      }}
                      onRequestStaff={() => {
                        setShowRequestForm(true);
                        setActiveView('requests');
                      }}
                    />
                    {selectedRequirementId && (
                      <div className="mt-6">
                        <h3 className="font-semibold mb-3">Alternative Staff</h3>
                        <AlternativeStaffSelector
                          requirementId={selectedRequirementId}
                          projectId={projectId}
                          currentStaffId={selectedStaffId}
                          onSelect={(staffId) => {
                            setSelectedStaffId(staffId);
                            setActiveView('calendar');
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            ),
          },
          {
            id: 'requests',
            label: 'Staff Requests',
            content: (
              <div className="space-y-6">
                {showRequestForm ? (
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => setShowRequestForm(false)}
                      className="mb-4"
                    >
                      ← Back to Requests
                    </Button>
                    <StaffRequestForm
                      projectId={projectId}
                      onSuccess={() => setShowRequestForm(false)}
                      onCancel={() => setShowRequestForm(false)}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Staff Request Workflow</h3>
                      <Button onClick={() => setShowRequestForm(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Request
                      </Button>
                    </div>
                    <StaffRequestWorkflow
                      projectId={projectId}
                    />
                  </>
                )}
              </div>
            ),
          },
        ]}
        defaultTab={activeView}
        onChange={(tabId) => setActiveView(tabId as any)}
      />

      {/* Info Tooltips */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Capacity Assessment & Conflict Resolution:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>FTE Calculation:</strong> 40 hours/week = 1.0 FTE (Full-Time Equivalent)</li>
              <li><strong>Capacity Warnings:</strong> Appear at 90% utilization to alert of approaching capacity limits</li>
              <li><strong>Conflicts:</strong> Detected when utilization exceeds 100% (configurable threshold)</li>
              <li><strong>Schedule Conflicts:</strong> Detected when assignments overlap by more than 1 day</li>
              <li><strong>Designation Gaps:</strong> Checked against RFP requirements from opportunity details</li>
              <li><strong>Staff Requests:</strong> Go through approval workflow (pending → under_review → approved/rejected → onboarding → completed)</li>
              <li><strong>Notifications:</strong> Sent at each stage to requester, reviewer, and admins</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
